<template>
    <div v-if="isVisible">
        <fullscreen-popup
            v-if="isFullscreen"
            :data="popup"
            :enabled="enabled"
            @close="onFullscreenClose"
            @submitted="onFullscreenSubmitted"
        />
        <popup
            v-else-if="hasEnoughSpace && enabled && !temporarilyHidden"
            v-bind="popup"
            :enabled="enabled"
            @closeButtonClick="onClose"
            @ctaButtonClick="closePopup"
        />
    </div>
</template>

<script lang="ts">
import { throttle } from 'lodash-es';
import { mapState } from 'vuex';
import breakpoint from '@core/mixins/breakpoint.js';
import { LOCALE_DEFAULT } from '@model/const/locales';
import FullscreenPopup from './fullscreen-popup.vue';
import Popup from './popup.vue';

const FULLSCREEN_DISPLAY_DELAY_MS = 1000 * 20; // 20 seconds
const FULLSCREEN_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 15; // 15 days

export default {
    components: { Popup, FullscreenPopup },
    mixins: [breakpoint],
    props: {
        delay: {
            type: Number,
            default: 10000,
        },
    },
    data: () => ({
        session: {
            shown: 'acronis_pb_shown',
        },
        local: {
            visible: 'acronis_pb_visible',
            closed: 'acronis_pb_banners',
            fsReEnable: 'acronis_pb_fs_re_enable',
            fsSubmitted: 'acronis_pb_fs_submitted',
        },
        viewedBannerIds: [],
        temporarilyHidden: false,
        enabled: false,
        hasEnoughSpace: true,
        timerId: null,
        selectedBannerIndex: -1,
        isReady: false,
        isDisplayBlocked: false,
    }),
    computed: {
        ...mapState({
            productID: (state) => state.pages?.page.product_id,
            promotions: (state: any) => state.promotions?.data || [],
        }),
        popups() {
            return this.$store.state.popup.data;
        },
        hasPopup() {
            return Boolean(this.popups.length);
        },
        popup() {
            return this.popups[this.selectedBannerIndex];
        },
        hasDiscountPromotion() {
            const compare = (item: any) => !item.is_for_emails_only && item.product_id === this.productID;
            const promotionItem = this.promotions.find(compare) || {};
            return Boolean(promotionItem.discount);
        },
        isFullscreen() {
            return this.popup.hasForm;
        },
        isVisible() {
            if (this.isMobile || !this.isReady || !this.hasPopup) return false;
            return !(this.hasDiscountPromotion && this.popup.hasForm);
        },
        isHomePage() {
            return this.$route?.path === `/${this.$store.state.geolocation.finalLocale}/`;
        },
        hasViewedBanner() {
            return this.viewedBannerIds.includes(this.popup.id);
        },
    },
    watch: {
        isVisible(value) {
            if (!value || !this.isDisplayBlocked) return;

            this.isDisplayBlocked = false;
            this.displayPopup();
        },
    },
    mounted() {
        if (!this.popups.length) return;

        if (typeof localStorage !== 'object') return;

        // During an active promotion the fullscreen (form) popups are excluded, regular banners are still shown
        const candidates = this.popups
            .map((popup: any, index: number) => ({ popup, index }))
            .filter(({ popup }: any) => !(this.hasDiscountPromotion && popup.hasForm));

        if (!candidates.length) return;

        this.selectedBannerIndex = candidates[Math.floor(Math.random() * candidates.length)].index;

        this.viewedBannerIds = JSON.parse(localStorage.getItem(this.local.closed)) || [];

        this.scrollHandler = throttle(() => {
            this.onLayoutChange();
            this.handleTemporaryHideOnScroll();
        }, 150);


        this.resizeHandler = throttle(this.onLayoutChange, 200);

        window.addEventListener('load', this.onLayoutChange, { passive: true });
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
        window.addEventListener('resize', this.resizeHandler, { passive: true });

        if (this.isFullscreen) {
            const isSubmitted = localStorage.getItem(`${this.local.fsSubmitted}_${this.popup.id}`) || false;
            const allowedTime = Number(localStorage.getItem(`${this.local.fsReEnable}_${this.popup.id}`) || 0);

            if (!isSubmitted && Date.now() >= allowedTime) {
                localStorage.removeItem(`${this.local.fsReEnable}_${this.popup.id}`);
                clearTimeout(this.timerId);
                this.timerId = setTimeout(this.displayPopup, FULLSCREEN_DISPLAY_DELAY_MS);
            }
        } else if (!this.hasViewedBanner) {
            if (this.isHomePage) {
                window.addEventListener('scroll', this.displayPopupAfterScroll, { passive: true });
            } else {
                clearTimeout(this.timerId);
                this.timerId = setTimeout(this.displayPopup, this.delay);
            }
        }

        this.isReady = true;
    },
    beforeUnmount() {
        clearTimeout(this.timerId);

        window.removeEventListener('load', this.onLayoutChange);
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('scroll', this.scrollHandler);
        window.removeEventListener('scroll', this.displayPopupAfterScroll);

        this.scrollHandler?.cancel?.();
        this.resizeHandler?.cancel?.();
    },
    methods: {
        handleTemporaryHideOnScroll() {
            if (this.isFullscreen || !this.enabled) return;

            const scrollY = window.scrollY || window.pageYOffset;
            const threshold = 600;

            /*
                We can shorten here to this.temporarilyHidden = scrollY < threshold
                But I think this will set the value on each scroll and that's why I tried to avoid that.
            */

            if (scrollY < threshold && !this.temporarilyHidden) {
                this.temporarilyHidden = true;
            } else if (scrollY >= threshold && this.temporarilyHidden) {
                this.temporarilyHidden = false;
            }
        },
        onLayoutChange() {
            const pageYOffset = window.pageYOffset;
            const scrollHeight = document.body.scrollHeight;
            const clientHeight = document.body.clientHeight;

            this.hasEnoughSpace = clientHeight > 480;
            if (!this.isFullscreen && pageYOffset > scrollHeight / 2) this.displayPopup();
        },
        closePopup() {
            this.disable();
        },
        onFullscreenSubmitted() {
            this.sendEventsToGA({ action: 'Submitted', label: 'Discount' });
            localStorage.setItem(`${this.local.fsSubmitted}_${this.popup.id}`, '1');
        },
        onFullscreenClose(isSubmitted) {
            if (isSubmitted) {
                localStorage.setItem(`${this.local.fsSubmitted}_${this.popup.id}`, '1');
            } else {
                localStorage.setItem(
                    `${this.local.fsReEnable}_${this.popup.id}`,
                    `${Date.now() + FULLSCREEN_COOLDOWN_MS}`,
                );
                this.sendEventsToGA({ action: 'Closed' });
            }
            this.closePopup();
        },
        onClose() {
            this.sendEventsToGA({ action: 'Closed' });
            this.closePopup();
        },
        disable() {
            this.enabled = false;
            this.viewedBannerIds.push(this.popup.id);

            localStorage.removeItem(this.local.visible);
            localStorage.setItem(this.local.closed, JSON.stringify(this.viewedBannerIds));

            clearTimeout(this.timerId);
        },
        enable() {
            if (this.hasViewedBanner) return;
            this.enabled = true;
            localStorage.setItem(this.local.visible, '1');
            clearTimeout(this.timerId);
        },
        displayPopupAfterScroll() {
            if (this.isFullscreen) {
                return;
            }

            const pageHeight = document.body.getBoundingClientRect().height;
            const halfPage = pageHeight / 2;

            if (window.scrollY >= halfPage) {
                this.displayPopup();
                window.removeEventListener('scroll', this.displayPopupAfterScroll);
            }
        },
        displayPopup() {
            if (this.enabled || this.hasViewedBanner) return;

            // The popup can't be rendered right now (e.g. mobile width), retry once it becomes visible
            if (!this.isVisible) {
                this.isDisplayBlocked = true;
                return;
            }

            sessionStorage.setItem(this.session.shown, '1');
            this.sendEventsToGA({ action: 'Shown' });
            this.enable();
        },

        sendEventsToGA(incomingGA) {
            const event = {
                event: 'Acronis',
                eventCategory: incomingGA.category || 'Popup',
                eventAction: incomingGA.action || '',
                eventLabel: incomingGA.label || this.popup?.ctaPrimaryUrl || window.location.href,
                eventContext: incomingGA.context || '',
                eventContent: incomingGA.content || this.popup.title,
                eventLocation: incomingGA.location || `${this.isFullscreen ? 'fullscreen' : 'bottom'} popup`,
                eventButton: incomingGA.button || '',
            };

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(event);
        },
    },

    async serverPrefetch() {
        if (!import.meta.env.SSR) return;

        const locale = this.$store.state.geolocation.finalLocale || LOCALE_DEFAULT;
        await Promise.all([
            this.$store.dispatch('slices/getSyncedData', { slice: 's-list-form-elements', locale }),
            this.$store.dispatch('slices/getSyncedData', { slice: 's-preset-form-special-offer', locale }),
        ]);
    },
};
</script>
